import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageShell } from "../components/Layout";
import SEO from "../components/SEO";
import { Sparkle, Calculator, ChatCircleDots, CheckCircle, Warning, MinusCircle } from "@phosphor-icons/react";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import PremiumTeaser from "../components/PremiumTeaser";
import { useAuth } from "../context/AuthContext";
import useBirthDetailsPrefill from "../hooks/useBirthDetailsPrefill";

// ---- Chaldean numerology tables ----------------------------------------------------
// Chaldean (a.k.a. Cheiro) — based on the SOUND vibration of each letter.
// Uses values 1–8 only; 9 is considered sacred and never assigned to a letter.
// Reference: Cheiro's "Book of Numbers" canonical mapping.
const LETTER_VALUES = {
  a: 1, i: 1, j: 1, q: 1, y: 1,
  b: 2, k: 2, r: 2,
  c: 3, g: 3, l: 3, s: 3,
  d: 4, m: 4, t: 4,
  e: 5, h: 5, n: 5, x: 5,
  u: 6, v: 6, w: 6,
  o: 7, z: 7,
  f: 8, p: 8,
};
const VOWELS = new Set(["a", "e", "i", "o", "u"]);
// Chaldean tradition does NOT use Pythagorean master numbers (11, 22, 33).
// We reduce the COMPOUND (raw) total all the way down to a single root 1–9,
// but the compound total itself is also reported because it carries meaning.

const reduceTo = (n) => {
  while (n > 9) {
    n = String(n).split("").reduce((s, d) => s + Number(d), 0);
  }
  return n;
};

const sumDigits = (s) =>
  String(s)
    .replace(/\D/g, "")
    .split("")
    .reduce((acc, d) => acc + Number(d), 0);

const computeLifePath = (dob /* YYYY-MM-DD */) => {
  if (!dob) return null;
  const [y, m, d] = dob.split("-").map((v) => Number(v));
  if (!y || !m || !d) return null;
  return reduceTo(reduceTo(sumDigits(d)) + reduceTo(sumDigits(m)) + reduceTo(sumDigits(y)));
};

const computeNameNumber = (name, pickerFn) => {
  if (!name) return null;
  const total = name
    .toLowerCase()
    .split("")
    .filter((ch) => /[a-z]/.test(ch))
    .filter(pickerFn ? (ch) => pickerFn(ch) : () => true)
    .reduce((acc, ch) => acc + (LETTER_VALUES[ch] || 0), 0);
  if (!total) return null;
  // Chaldean reports BOTH the compound total (carries meaning) AND the
  // reduced single-digit root. The reducer renderer can use either.
  return { compound: total, root: reduceTo(total) };
};

// Convenience for legacy call-sites that only want a single digit
const rootOf = (val) => (val && typeof val === "object" ? val.root : val);

const NUMBER_PROFILE = {
  1: { name: "The Leader", ruler: "Sun", color: "#F59E0B", traits: ["Independent", "Ambitious", "Pioneering"], gist: "Born to lead. Drives initiatives, hates being micromanaged, thrives in entrepreneurial or executive roles." },
  2: { name: "The Diplomat", ruler: "Moon", color: "#3B82F6", traits: ["Sensitive", "Cooperative", "Intuitive"], gist: "Excels in partnerships, mediation and creative team work. Highly empathic — needs harmony to flourish." },
  3: { name: "The Communicator", ruler: "Jupiter", color: "#A855F7", traits: ["Expressive", "Creative", "Optimistic"], gist: "Artistic, charming and verbally gifted. Best when free to create, perform or teach." },
  4: { name: "The Builder", ruler: "Rahu", color: "#64748B", traits: ["Disciplined", "Practical", "Reliable"], gist: "Methodical and hardworking. Excellent with systems, structure and long-term planning." },
  5: { name: "The Free Spirit", ruler: "Mercury", color: "#10B981", traits: ["Adventurous", "Versatile", "Quick-witted"], gist: "Loves change, travel and variety. Thrives in sales, media and anything that lets them roam." },
  6: { name: "The Nurturer", ruler: "Venus", color: "#EC4899", traits: ["Caring", "Responsible", "Artistic"], gist: "Family-oriented, beauty-loving. Strong in healing, hospitality, design and counselling roles." },
  7: { name: "The Mystic", ruler: "Ketu", color: "#8B5CF6", traits: ["Analytical", "Spiritual", "Introspective"], gist: "Deep thinker drawn to research, science and inner work. Needs solitude to recharge." },
  8: { name: "The Powerhouse", ruler: "Saturn", color: "#1F2937", traits: ["Ambitious", "Disciplined", "Karmic"], gist: "Tied to material mastery and lessons of karma. Either rises high or learns through hard tests — rarely middle ground." },
  9: { name: "The Humanitarian", ruler: "Mars", color: "#EF4444", traits: ["Compassionate", "Brave", "Idealistic"], gist: "Driven to serve a larger cause. Powerful in leadership, the armed forces, medicine and activism." },
};

// Compound (raw) name-total notes — Chaldean tradition assigns specific
// meanings to selected compound numbers up to 52. We surface the most
// commonly-cited ones for added depth on the name read.
const COMPOUND_NOTES = {
  10: "Wheel of Fortune — strong willpower, success follows effort.",
  11: "Hidden warnings — be cautious of betrayal; rely on inner strength.",
  12: "The Sacrifice — patience and selfless service bring rewards.",
  13: "Upheaval — power to break old patterns, but disruptive vibration.",
  14: "Movement and challenge — speculation risky, beware overconfidence.",
  15: "Magnetism and arts — favours creative, romantic, financial gains.",
  16: "The Shattered Citadel — strong karmic warning; ego must yield.",
  17: "The Star of the Magi — spiritual evolution, timeless name.",
  18: "Material-spiritual struggle — bitter quarrels, exercise caution.",
  19: "The Prince of Heaven — happiness, success, honour.",
  20: "The Awakening — vision, divine call to action.",
  21: "The Crown of the Magi — success, advancement, victory.",
  22: "Submission and good nature — beware naïveté in trust.",
  23: "The Royal Star of the Lion — protection, success in any venture.",
  24: "Strong romantic luck, help from those above one's station.",
  25: "Strength gained through learning hard lessons.",
  26: "Partnership warnings — beware bad advice and ruinous association.",
  27: "The Sceptre — authority and command earned through wisdom.",
  28: "Loss through trust — strong start but uneven endings.",
  29: "Uncertainties, deceit from others — choose company carefully.",
  30: "Mental and intellectual superiority over others.",
  31: "Lonely vibration — content alone, less social.",
  32: "Magical power — communication and crowds favour you.",
  33: "Echoes 24 — strong romantic and financial favour.",
  34: "Echoes 25 — strength through experience and hard knocks.",
  35: "Echoes 26 — bad advice warning, partnerships shaky.",
  36: "Echoes 27 — earned authority and respect.",
  37: "Favours friendships, love, partnerships — luck in unions.",
  43: "Revolution, upheaval, conflict — avoid impulsive moves.",
  51: "The warrior — sudden advancement followed by reversals.",
};

const baseOf = (n) => n;

// Friend / neutral / enemy table (Cheiro-style, simplified)
const FRIENDLY = {
  1: new Set([1, 3, 5, 9]),
  2: new Set([2, 7, 9]),
  3: new Set([1, 3, 5, 6, 9]),
  4: new Set([5, 6]),
  5: new Set([1, 3, 5, 6, 9]),
  6: new Set([3, 4, 5, 9]),
  7: new Set([1, 2, 4, 9]),
  8: new Set([3, 5, 6]),
  9: new Set([1, 3, 5, 6, 9]),
};
const ENEMY = {
  1: new Set([4, 8]),
  2: new Set([1, 5]),
  3: new Set([4, 8]),
  4: new Set([1, 8, 9]),
  5: new Set([2]),
  6: new Set([7, 8]),
  7: new Set([8]),
  8: new Set([1, 2, 4, 9]),
  9: new Set([4, 8]),
};

const compatibilityVerdict = (nameVal, lifeN) => {
  const a = baseOf(rootOf(nameVal));
  const b = baseOf(lifeN);
  const nameDisplay = nameVal && typeof nameVal === "object"
    ? `${nameVal.compound} → ${nameVal.root}`
    : String(nameVal);
  if (a === b) return {
    score: "harmonious",
    title: "Excellent fit",
    verdict_color: "emerald",
    detail: `Your name vibration (${nameDisplay}) resonates with the same energy as your life path (${lifeN}). This is the strongest possible alignment — your name actively amplifies who you came here to be.`,
  };
  if (FRIENDLY[a]?.has(b) || FRIENDLY[b]?.has(a)) return {
    score: "friendly",
    title: "Supportive fit",
    verdict_color: "emerald",
    detail: `Your name (${nameDisplay}) is in a friendly vibration with your life path (${lifeN}). The two energies support each other — your name will largely help, not hinder, your life's mission.`,
  };
  if (ENEMY[a]?.has(b) || ENEMY[b]?.has(a)) return {
    score: "challenging",
    title: "Challenging fit",
    verdict_color: "rose",
    detail: `Your name vibration (${nameDisplay}) is in dissonance with your life path (${lifeN}). This can show up as recurring obstacles in career, finances or relationships despite hard work. A name correction (Chaldean-based) is often recommended in this case.`,
  };
  return {
    score: "neutral",
    title: "Neutral fit",
    verdict_color: "amber",
    detail: `Your name (${nameDisplay}) is neither strongly aligned nor opposed to your life path (${lifeN}). It is a workable combination but won't actively boost your karmic mission either.`,
  };
};

// ---- Component -------------------------------------------------------------------------
export default function NameNumerologyPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");

  // One-shot prefill from signed-in user profile (shared hook)
  useBirthDetailsPrefill((bd, u) => {
    if (u?.name) setName((prev) => prev || u.name);
    if (bd?.date) setDob((prev) => prev || bd.date);
  });

  const result = useMemo(() => {
    const lp = computeLifePath(dob);
    const expr = computeNameNumber(name);
    const soul = computeNameNumber(name, (c) => VOWELS.has(c));
    const personality = computeNameNumber(name, (c) => !VOWELS.has(c));
    if (!lp || !expr) return null;
    const fit = compatibilityVerdict(expr, lp);
    return { life_path: lp, expression: expr, soul_urge: soul, personality, fit };
  }, [name, dob]);

  const tooltip = (n) => NUMBER_PROFILE[n] || { name: "—", traits: [], gist: "" };

  return (
    <PageShell>
      <SEO
        title="Name Numerology Calculator — Free Chaldean / Cheiro Method"
        description="Free Chaldean numerology calculator. Decode the sound vibration of your name against your birth date — Life Path, Expression, Soul-Urge, Personality and compound + root numbers using Cheiro's classical 1–8 mapping."
        path="/name-numerology"
        keywords={[
          "numerology calculator", "free numerology", "chaldean numerology",
          "cheiro numerology", "name numerology", "life path number",
          "expression number", "soul urge number", "personality number",
          "destiny number calculator", "lucky number calculator", "name correction",
        ]}
      />
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 sm:pt-16 pb-24" data-testid="name-numerology-page">
        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Calculator size={14} weight="duotone" /> Free · Chaldean numerology
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light tracking-tight leading-[1.1]">
          Name Numerology <span className="italic text-gold">Calculator</span>
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-2xl font-light">
          Decode the vibration of your name against your birth date using the <b className="text-[#3F2D1B]">Chaldean (Cheiro) method</b> — based on the SOUND vibration of each letter. More precise than the Pythagorean alphabetical system. We compute your Life Path, Expression (compound + root), Soul-Urge and Personality numbers and tell you whether your current name is helping — or quietly holding you back.
        </p>

        <div className="grid lg:grid-cols-12 gap-8 mt-10">
          {/* INPUT */}
          <div className="card-surface rounded-md p-5 sm:p-7 lg:col-span-5 self-start" data-testid="numerology-form">
            <div className="flex items-center gap-2 text-gold label-eyebrow">
              <Sparkle size={14} weight="fill" /> Your details
            </div>
            <div className="mt-5 space-y-4">
              <div>
                <label className="label-eyebrow">Full name (as you go by)</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Anita Sharma"
                  className="w-full mt-2 px-3 py-2.5 border border-soft rounded-md focus:border-gold outline-none text-sm"
                  data-testid="nn-name-input"
                />
                <p className="text-muted text-[11px] mt-1.5">Use the name you currently use in life, not your legal birth name (if different).</p>
              </div>
              <div>
                <label className="label-eyebrow">Date of birth</label>
                <input
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  className="w-full mt-2 px-3 py-2.5 border border-soft rounded-md focus:border-gold outline-none text-sm bg-transparent"
                  data-testid="nn-dob-input"
                />
              </div>
            </div>
            <p className="text-muted text-[11px] mt-5 font-light">
              We never save your details on this page. Sign in if you want a personalised, ongoing reading.
            </p>
          </div>

          {/* RESULT */}
          <div className="lg:col-span-7">
            {!result ? (
              <div className="card-surface rounded-md p-8 sm:p-12 text-center h-full flex flex-col items-center justify-center gap-3" data-testid="nn-placeholder">
                <Calculator size={40} weight="duotone" className="text-gold opacity-70" />
                <p className="font-serif-display text-2xl sm:text-3xl font-light">Your numerology reading will open here</p>
                <p className="text-muted text-sm max-w-md">
                  Enter your name and date of birth — we&apos;ll instantly reveal the four core numbers and whether your name aligns with your life mission.
                </p>
              </div>
            ) : (
              <div className="space-y-5 anim-fadeup" data-testid="nn-result">
                {/* Headline verdict */}
                <div className={`card-surface rounded-md p-6 sm:p-8 border-2 ${
                  result.fit.verdict_color === "emerald" ? "border-emerald-300" :
                  result.fit.verdict_color === "amber" ? "border-amber-300" :
                  "border-rose-300"
                }`} data-testid="nn-verdict">
                  <div className="label-eyebrow text-gold">Name ↔ Birth date alignment</div>
                  <div className="flex items-start gap-3 mt-2">
                    {result.fit.verdict_color === "emerald" ? <CheckCircle size={28} weight="fill" className="text-emerald-600 flex-shrink-0" /> :
                     result.fit.verdict_color === "amber" ? <MinusCircle size={28} weight="fill" className="text-amber-600 flex-shrink-0" /> :
                     <Warning size={28} weight="fill" className="text-rose-600 flex-shrink-0" />}
                    <div>
                      <div className="font-serif-display text-2xl sm:text-3xl font-light">{result.fit.title}</div>
                      <p className="text-muted text-sm mt-2 font-light leading-relaxed">{result.fit.detail}</p>
                    </div>
                  </div>
                </div>

                {/* 4 core numbers */}
                <div className="grid sm:grid-cols-2 gap-3" data-testid="nn-numbers">
                  <NumberCard label="Life Path" sub="From your DOB" number={result.life_path} primary />
                  <NumberCard label="Expression" sub="Compound + root, full Chaldean name" number={result.expression} compoundNote={COMPOUND_NOTES[result.expression?.compound]} primary />
                  <NumberCard label="Soul Urge" sub="From the vowels in your name" number={result.soul_urge} />
                  <NumberCard label="Personality" sub="From the consonants" number={result.personality} />
                </div>

                {/* Insight cards */}
                <div className="card-surface rounded-md p-5 sm:p-7" data-testid="nn-insights">
                  <div className="label-eyebrow text-gold">What these mean for you</div>
                  <InsightRow title={`Life Path ${result.life_path}`} profile={tooltip(result.life_path)}
                    line="Your blueprint for this lifetime — your innate strengths and the lessons you came here to master." />
                  <InsightRow title={`Expression ${rootOf(result.expression)} (compound ${result.expression?.compound})`} profile={tooltip(rootOf(result.expression))}
                    line="How the world experiences you, your talents and your public destiny. The Chaldean compound number carries additional flavour."
                    extra={COMPOUND_NOTES[result.expression?.compound] && `Compound ${result.expression.compound} — ${COMPOUND_NOTES[result.expression.compound]}`} />
                  <InsightRow title={`Soul Urge ${rootOf(result.soul_urge)}`} profile={tooltip(rootOf(result.soul_urge))}
                    line="What your heart secretly wants — the inner drive most people don't see." />
                  <InsightRow title={`Personality ${rootOf(result.personality)}`} profile={tooltip(rootOf(result.personality))}
                    line="The first impression you give off — the persona others meet before they know you." last />
                </div>

                {/* Funnel teaser — numerology users → 20-Year Report */}
                <PremiumTeaser
                  testid="nn-premium-teaser"
                  topLine={<>
                    Numbers hint. Your chart <em className="not-italic text-amber-300">tells</em>.<br/>
                    <span className="text-amber-300">Get your 55+ page life story.</span>
                  </>}
                  subLine={<>
                    Your Life Path <b className="text-amber-100">{result.life_path}</b> pairs with a much bigger
                    Vedic map — Sade Sati windows, 6 doshas, gem &amp; rudraksha, personalised dasha-bhukti
                    timeline and year-by-year career / marriage / wealth / health scores over the next 20 years.
                  </>}
                />

                {/* CTA — Chaldean + Lo Shu specialist */}
                <ToolAstrologerCta
                  requiredTechnique="Numerology + Lo Shu Grid"
                  pitch={result.fit.verdict_color === "rose"
                    ? `Your name may be draining energy — get a live name-spelling correction by a Chaldean + Lo Shu expert`
                    : `Deepen your reading — Personal Year, lucky dates & Lo Shu remedies with a Chaldean specialist`}
                  ctaLabel="Talk to"
                />
                <div className="text-center">
                  <button
                    onClick={() => navigate("/kundali-calculator")}
                    className="btn-ghost rounded-full px-6 py-3 font-medium text-sm"
                  >
                    Get your Vedic kundali too →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

function NumberCard({ label, sub, number, primary, compoundNote }) {
  // `number` can be a plain int (Life Path) OR a {compound, root} object (Expression/Soul/Personality).
  const isObj = number && typeof number === "object";
  const root = isObj ? number.root : number;
  const compound = isObj ? number.compound : null;
  const p = NUMBER_PROFILE[root] || {};
  return (
    <div className={`card-surface rounded-md p-4 sm:p-5 ${primary ? "border-2 border-[#D4AF37]/30" : ""}`} data-testid={`nn-card-${label.toLowerCase().replace(/ /g, '-')}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="label-eyebrow !text-[10px]">{label}</div>
          <div className="text-muted text-[10px] mt-0.5">{sub}</div>
        </div>
        <div className="font-serif-display text-4xl sm:text-5xl font-light text-gold leading-none flex items-baseline gap-1.5">
          {compound != null && compound !== root && (
            <span className="text-xl sm:text-2xl text-gold/60">{compound} →</span>
          )}
          <span>{root}</span>
        </div>
      </div>
      <div className="mt-3 text-xs sm:text-sm">
        <span className="font-medium text-[#3F2D1B]">{p.name}</span>
        {p.ruler && <span className="text-muted"> · ruled by {p.ruler}</span>}
      </div>
      {compoundNote && (
        <div className="mt-2 text-[11px] sm:text-xs text-[#3F2D1B]/80 italic leading-snug">
          ✦ {compoundNote}
        </div>
      )}
      {p.traits && p.traits.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {p.traits.map((t, i) => (
            <span key={i} className="text-[10px] px-2 py-0.5 rounded-full border border-soft text-muted">{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function InsightRow({ title, profile, line, extra, last }) {
  return (
    <div className={`mt-4 ${last ? "" : "border-b border-soft pb-4"}`}>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="font-medium text-sm">{title}</span>
        <span className="text-muted text-xs">— {profile.name}</span>
      </div>
      <p className="text-muted text-xs mt-1 leading-relaxed">{line}</p>
      {profile.gist && <p className="text-[#3F2D1B] text-xs mt-1.5 italic leading-relaxed">&ldquo;{profile.gist}&rdquo;</p>}
      {extra && <p className="text-[#7C2D12] text-xs mt-1.5 leading-relaxed">{extra}</p>}
    </div>
  );
}
